import type {
  BookingPassenger,
  BookingRoom,
  HotelAvailabilityRoom,
  ProviderRoomOccupancy,
} from '../api/hotels.types';
import type { CombinationRoom } from '../types/hotels.presentation';

/** Guests of one physical room of the booking, in the order the provider quoted them. */
export interface BookingRoomLayout {
  id: number;
  adults: number;
  children: number;
  childrenAges: number[];
}

function toCount(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : 0;
}

function toAges(occupancy: ProviderRoomOccupancy | undefined): number[] {
  return (occupancy?.ages ?? []).map(Number).filter((age) => Number.isFinite(age));
}

/** One layout per physical room of a combination (entries with `units > 1` expand). */
function layoutFromCombination(rooms: CombinationRoom[]): Omit<BookingRoomLayout, 'id'>[] {
  return rooms.flatMap((room) =>
    Array.from({ length: Math.max(1, room.units) }, () => ({
      adults: room.adults,
      children: room.children,
      childrenAges: room.childrenAges,
    })),
  );
}

/**
 * Resolves how many rooms the booking has and who sleeps in each one.
 *
 * The availability quote is the source of truth: it returns one `hotel.rooms[]` entry per
 * physical room (ids 1..N) with its occupancy. When it lacks occupancy, the selected
 * combination's rooms fill it in; with neither, all guests go in a single room.
 */
export function resolveBookingRoomLayout(
  availabilityRooms: HotelAvailabilityRoom[] | undefined,
  combinationRooms: CombinationRoom[] | undefined,
  fallback: { adults: number; children: number },
): BookingRoomLayout[] {
  const fromCombination = layoutFromCombination(combinationRooms ?? []);

  if (availabilityRooms?.length) {
    return availabilityRooms.map((room, index) => {
      const quoted = room.occupancy;
      const known = fromCombination[index];
      const adults = toCount(quoted?.adults) || known?.adults || 0;
      const childrenAges = quoted ? toAges(quoted) : (known?.childrenAges ?? []);
      const children = toCount(quoted?.children) || known?.children || childrenAges.length;
      return { id: room.id, adults: Math.max(1, adults), children, childrenAges };
    });
  }

  if (fromCombination.length > 0 && fromCombination.every((room) => room.adults > 0)) {
    return fromCombination.map((room, index) => ({ id: index + 1, ...room }));
  }

  return [
    {
      id: 1,
      adults: Math.max(1, fallback.adults || 1),
      children: Math.max(0, fallback.children || 0),
      childrenAges: [],
    },
  ];
}

function withExtraFields(
  passengers: BookingPassenger[],
  availabilityRoom: HotelAvailabilityRoom | undefined,
): BookingPassenger[] {
  const extraFieldsByPax = availabilityRoom?.extraFields ?? {};

  return passengers.map((passenger, index) => {
    const extraFields = extraFieldsByPax[`pax${index + 1}`];

    if (!Array.isArray(extraFields) || extraFields.length === 0) return passenger;

    return { ...passenger, extraFields };
  });
}

/**
 * Builds the `rooms` array sent to book/inquiry, attaching to each passenger the
 * `extraFields` keys the availability quote requested for its position within its own
 * room (`pax1` → first passenger of that room, `pax2` → second, …). Values are never
 * filled in by the frontend.
 */
export function buildBookingRooms(
  rooms: BookingRoom[],
  availabilityRooms?: HotelAvailabilityRoom[],
): BookingRoom[] {
  return rooms.map((room, index) => {
    const availabilityRoom =
      availabilityRooms?.find((candidate) => candidate.id === room.id) ??
      (rooms.length === 1 ? availabilityRooms?.[0] : availabilityRooms?.[index]);

    return { id: room.id, passengers: withExtraFields(room.passengers, availabilityRoom) };
  });
}
